import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from 'firebase/auth';
import { auth } from './firebase';
import type { MultiFactorResolver } from 'firebase/auth';
import { isMfaRequiredError, mfaResolver, completeMfaSignIn, signInErrorText } from './mfa-signin';
import { Wordmark } from './brand';
import { App } from './App';
import { refreshMyClaims } from './api';
import { UpdateGate } from './update-check';
import { initTheme } from './theme';
import './styles.css';
import './admin.css';

function Root() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => onAuthStateChanged(auth, async (u) => {
    setUser(u);
    if (!u) { setIsAdmin(null); setReady(true); return; }

    // The platformAdmin claim is a cache of the platformAdmins document. If it
    // has not been minted yet — or was granted after this token — re-derive it
    // once before locking someone out of their own control plane.
    let token = await u.getIdTokenResult();
    if (token.claims.platformAdmin !== true) {
      try {
        await refreshMyClaims({});
        token = await u.getIdTokenResult(true);
      } catch { /* fall through; the gate below explains it */ }
    }
    setIsAdmin(token.claims.platformAdmin === true);
    setReady(true);
  }), []);


  if (!ready) return <div className="boot">Loading…</div>;
  if (!user) return <SignIn />;

  if (isAdmin === false) {
    return (
      <div className="signin-wrap">
        <div className="signin-card">
          <Wordmark width={190} />
          <div className="brand-name" style={{ marginTop: 10 }}>Not a platform admin</div>
          <p className="panel-note" style={{ marginTop: 4 }}>
            {user.email} is signed in, but this console is restricted to platform
            administrators. If you are looking for your backup dashboard, use the main app.
          </p>
          <button className="btn" onClick={() => void signOut(auth)}>Sign out</button>
        </div>
      </div>
    );
  }

  return <App email={user.email} />;
}

/**
 * Signing in to the platform console.
 *
 * THIS FORM USED TO BE UNABLE TO REPORT WHAT WENT WRONG. It wrapped
 * signInWithEmailAndPassword in a bare catch and printed one sentence for every
 * possible failure - so an account with an authenticator app enrolled, which
 * this very console flags as a risk when ABSENT, was told its password was
 * wrong. It was not. Firebase was asking for the six digits and there was
 * nowhere to type them.
 *
 * The generic message survives for exactly one case - bad credentials - because
 * separating "no such account" from "wrong password" turns a login form into a
 * way to discover which addresses exist. Every other cause is named, because
 * every other cause has a different fix.
 */
function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Set when Firebase interrupts sign-in for the second factor. */
  const [pending, setPending] = useState<MultiFactorResolver | null>(null);
  const [code, setCode] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      // NOT a failure: the password was right and Firebase wants the factor. No
      // session exists yet - the resolver carries the pending sign-in and it
      // completes only once the code checks out.
      if (isMfaRequiredError(err)) {
        setPending(mfaResolver(err));
        setBusy(false);
        return;
      }
      setError(signInErrorText(err));
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (!pending) return;
    setBusy(true); setError(null);
    try {
      await completeMfaSignIn(pending, code);
    } catch (err) {
      /*
       * THROUGH THE MAPPER, like the password failure above.
       *
       * This line printed err.message, and a FirebaseError's message is the
       * raw "Firebase: Error (auth/invalid-verification-code)." - which is
       * precisely the string Damon was shown. It names no cause and suggests
       * no action, and for the commonest cause (a device clock a couple of
       * minutes out) it points nowhere near the fix.
       */
      setError(signInErrorText(err));
      setBusy(false);
    }
  }

  if (pending) {
    return (
      <div className="signin-wrap">
        <form className="signin-card" onSubmit={verify}>
          <div className="signin-brand">
            <Wordmark width={210} />
            <div className="brand-sub">Two-factor - {email}</div>
          </div>
          {/*
            * A HIDDEN USERNAME FIELD, AND IT IS NOT DECORATION.
            *
            * Damon: "code for auth app will not pull from 1password. user and
            * pass does." The reason is that this is a SEPARATE form with no
            * username on it, so a password manager has nothing to match against
            * the vault item holding the TOTP secret - it does not know which
            * login this six-digit box belongs to.
            *
            * The fix every password manager understands is to put the username
            * back in the form, hidden, with autocomplete="username". 1Password
            * then associates the form with the same item and offers the code.
            *
            * NOT type="hidden": managers skip hidden inputs entirely. It has to
            * be a real, focusable field moved out of sight, which is what
            * .visually-hidden does. readOnly and tabIndex=-1 keep it out of the
            * way of anybody typing or tabbing.
            */}
          <input
            className="visually-hidden" type="email" name="username" autoComplete="username"
            value={email} readOnly tabIndex={-1} aria-hidden="true"
          />
          <label className="field">
            <span className="field-label">Code from your authenticator app</span>
            <input
              className="field-input"
              /* name and id both 'otp': managers match on these as well as on
                 autocomplete, and the extra hint costs nothing. */
              name="otp" id="otp"
              inputMode="numeric" autoComplete="one-time-code"
              maxLength={6} required autoFocus
              /*
               * DIGITS ONLY, AND THE TRUNCATION IS WHY.
               *
               * Authenticator apps and password managers commonly present a
               * code as "123 456". Pasted or typed with that space, maxLength
               * 6 cut it to "123 45" - still six characters, so the button
               * enabled, and a mangled code went to Firebase and came back
               * invalid-verification-code. Nothing on screen suggested the
               * value had been altered; the field simply showed a short code
               * that looked complete.
               *
               * .trim() in completeMfaSignIn could never have caught it: the
               * space is in the MIDDLE. Stripping non-digits here also covers
               * a pasted code that arrives with a newline or a zero-width
               * character, which is common from a password manager.
               *
               * The tenant's enrolment field has done exactly this since MFA
               * shipped (MfaCard.tsx). This is the copy that did not.
               */
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </label>
          {error && <div className="signin-error">{error}</div>}
          <button className="btn btn-primary" type="submit" disabled={busy || code.trim().length < 6}>
            {busy ? 'Checking.' : 'Verify'}
          </button>
          <button className="btn-link" type="button"
                  onClick={() => { setPending(null); setCode(''); setError(null); }}>
            Use a different account
          </button>
          <div className="signin-version">v{__APP_VERSION__}</div>
        </form>
      </div>
    );
  }

  return (
    <div className="signin-wrap">
      <form className="signin-card" onSubmit={submit}>
        <div className="signin-brand">
          {/* The lockup, not the bare name. This is the first screen anybody
              sees and the one a phishing page would imitate; a real mark that
              renders instantly is part of how somebody knows they are in the
              right place. */}
          <Wordmark width={210} />
          <div className="brand-sub">Platform Admin</div>
        </div>
        <label className="field">
          <span className="field-label">Email</span>
          <input className="field-input" type="email" autoComplete="username" required
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Password</span>
          <input className="field-input" type="password" autoComplete="current-password" required
                 value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && <div className="signin-error">{error}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div className="signin-version">v{__APP_VERSION__}</div>
      </form>
    </div>
  );
}

initTheme();

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
    {/* Sibling of the app: it must survive every view change and show even when
        nobody is signed in — a stale build is exactly what would stop someone
        signing in at all. */}
    <UpdateGate />
  </React.StrictMode>
);
